import { useState, useEffect, Fragment } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

// Groups the real permission strings (Module.Action, e.g. "PO.Create")
// by their Module prefix for display -- no hardcoded permission list
// here, the actual set of permissions is loaded from role_permissions
// itself (see loadData()) so this page can never drift out of sync
// with what's really in the database, and never invents a permission
// that doesn't exist.
function groupByModule(permissions) {
  const groups = {}
  permissions.forEach(p => {
    const [module] = p.split('.')
    if (!groups[module]) groups[module] = []
    groups[module].push(p)
  })
  return groups
}

export default function PermissionsPage() {
  const { can } = useAuth()
  const [roles, setRoles] = useState([])           // [{ id, code }]
  const [permissions, setPermissions] = useState([]) // ["PO.Create", ...] distinct, from DB
  const [grants, setGrants] = useState({})          // { [role_id]: { [permission]: granted } }
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    const [{ data: rolesData, error: rolesErr }, { data: rpData, error: rpErr }] = await Promise.all([
      supabase.from('roles').select('id, code').order('code'),
      // role_permissions is the real, current source of truth for
      // which permissions exist at all -- distinct permission strings
      // are derived from it below rather than a hardcoded list.
      supabase.from('role_permissions').select('role_id, permission, granted'),
    ])
    if (rolesErr || rpErr) {
      setError((rolesErr || rpErr).message)
      setLoading(false)
      return
    }
    const distinctPerms = [...new Set((rpData || []).map(r => r.permission))].sort()
    const map = {}
    ;(rolesData || []).forEach(r => { map[r.id] = {} })
    ;(rpData || []).forEach(row => {
      if (!map[row.role_id]) map[row.role_id] = {}
      map[row.role_id][row.permission] = row.granted
    })
    setRoles(rolesData || [])
    setPermissions(distinctPerms)
    setGrants(map)
    setLoading(false)
  }

  function toggle(roleId, permission) {
    setGrants(prev => ({
      ...prev,
      [roleId]: { ...prev[roleId], [permission]: !prev[roleId]?.[permission] }
    }))
  }

  function toggleAllRole(roleId) {
    const allOn = permissions.every(p => grants[roleId]?.[p])
    setGrants(prev => ({
      ...prev,
      [roleId]: Object.fromEntries(permissions.map(p => [p, !allOn]))
    }))
  }

  function toggleAllPermission(permission) {
    const allOn = roles.every(r => grants[r.id]?.[permission])
    setGrants(prev => {
      const next = { ...prev }
      roles.forEach(r => { next[r.id] = { ...next[r.id], [permission]: !allOn } })
      return next
    })
  }

  async function savePerms() {
    setSaving(true)
    setError(null)
    const rows = []
    roles.forEach(r => {
      permissions.forEach(p => {
        rows.push({ role_id: r.id, permission: p, granted: grants[r.id]?.[p] ?? false })
      })
    })
    // role_id + permission is the real unique constraint on
    // role_permissions (NOT the non-existent "role" text column the
    // previous version of this page assumed).
    const { error: upsertErr } = await supabase
      .from('role_permissions')
      .upsert(rows, { onConflict: 'role_id,permission' })
    if (upsertErr) {
      setError(upsertErr.message)
      setSaving(false)
      return
    }
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  if (!can.is_admin) return (
    <div style={{padding:60,textAlign:'center',color:'#9A948C',fontSize:13}}>Access denied — Admin only</div>
  )

  if (loading) return <div style={{padding:40,textAlign:'center',color:'#9A948C',fontSize:13}}>Loading…</div>

  const moduleGroups = groupByModule(permissions)
  const moduleNames = Object.keys(moduleGroups).sort()

  return (
    <div style={{minHeight:'100vh',background:'#F5F1EA',fontFamily:'Inter,sans-serif'}}>
      {/* Header */}
      <div style={{background:'#F5F1EA',borderBottom:'1px solid #DDD9D0',padding:'20px 32px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.14em',color:'#9A948C',marginBottom:4}}>Admin</p>
          <h1 style={{fontSize:22,fontWeight:800,color:'#1C1A18'}}>Role Permissions</h1>
        </div>
        <button onClick={savePerms} disabled={saving}
          style={{padding:'9px 20px',background:saved?'#15803d':'#1A7A4A',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',transition:'background .2s'}}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div style={{margin:'16px 32px 0',padding:'10px 16px',borderRadius:8,background:'#fef2f2',border:'1px solid #fecaca',fontSize:12,color:'#991b1b'}}>
          {error}
        </div>
      )}

      <div style={{padding:'24px 32px',overflowX:'auto'}}>
        <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,overflow:'hidden',minWidth:900}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#1C1A18'}}>
                <th style={{padding:'10px 14px',textAlign:'left',color:'#9A948C',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',width:220,position:'sticky',left:0,background:'#1C1A18'}}>Permission</th>
                {roles.map(r => (
                  <th key={r.id} style={{padding:'8px 6px',textAlign:'center',color:'#fff',fontSize:10,fontWeight:700,cursor:'pointer',minWidth:100}}
                    onClick={() => toggleAllRole(r.id)}
                    title="Click to toggle all">
                    <div style={{fontSize:10,lineHeight:1.3}}>{r.code}</div>
                    <div style={{fontSize:9,color:'#6B655C',marginTop:2}}>click all</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {moduleNames.map(moduleName => (
                <Fragment key={moduleName}>
                  <tr style={{background:'#F5F1EA'}}>
                    <td colSpan={roles.length + 1} style={{padding:'6px 14px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#6B655C'}}>{moduleName}</td>
                  </tr>
                  {moduleGroups[moduleName].map(permission => (
                    <tr key={permission} style={{borderBottom:'1px solid #F5F1EA'}}>
                      <td style={{padding:'8px 14px',color:'#1C1A18',fontWeight:500,position:'sticky',left:0,background:'#fff',cursor:'pointer'}}
                        onClick={() => toggleAllPermission(permission)}
                        title="Click to toggle all roles">
                        {permission}
                      </td>
                      {roles.map(r => {
                        const granted = grants[r.id]?.[permission] ?? false
                        return (
                          <td key={r.id} style={{textAlign:'center',padding:'8px 6px'}}>
                            <div onClick={() => toggle(r.id, permission)}
                              style={{width:20,height:20,borderRadius:4,border:`2px solid ${granted?'#1A7A4A':'#DDD9D0'}`,background:granted?'#1A7A4A':'#fff',cursor:'pointer',margin:'0 auto',display:'flex',alignItems:'center',justifyContent:'center',transition:'all .15s'}}>
                              {granted && <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{fontSize:11,color:'#9A948C',marginTop:12}}>Click a column header to toggle all permissions for a role. Click a row label to toggle all roles for a permission.</p>
      </div>
    </div>
  )
}
